import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { PanelComponent } from '@components/core/panel/panel.component';
import { EvaluationService } from '@services/core/evaluation.service';
import { NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DATA_TYPE_OPTIONS } from '../../../shared/question-types/question-types.registry';
import { buildTypePayload, confirmRemoveType, isTypeFormInvalid, showTypeError } from './question-types-helpers';

@Component({
  selector: 'app-question-types',
  templateUrl: './question-types.component.html',
  styleUrls: ["./question-types.component.scss"],
  imports: [PanelComponent, FormsModule, ReactiveFormsModule, NgbTooltipModule],
  providers: [EvaluationService],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
})
export class QuestionTypesComponent implements OnInit {
  protected types: any[] = []
  protected dataTypeOptions = DATA_TYPE_OPTIONS

  private evaluationService = inject(EvaluationService)
  private modalService = inject(NgbModal)
  private fb = inject(FormBuilder)
  private destroyRef = inject(DestroyRef)
  private modalOptions = { size: 'lg', scrollable: true, animation: true, backdrop: null }

  protected typeForm = this.fb.group({
    id: [null],
    dataType: ['text', Validators.required],
    label: ['', Validators.required],
    regex: [''],
    enabled: [true],
  })

  ngOnInit() {
    this.loadTypes();
  }

  loadTypes() {
    this.evaluationService.getQuestionTypes().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.types = res;
    });
  }

  open(modal: any, type?: any) {
    if (type) this.typeForm.patchValue(type);
    else this.typeForm.reset({ dataType: 'text', regex: '', enabled: true });
    this.modalService.open(modal, this.modalOptions).result.then(() => {}, () => {});
  }

  save() {
    if (isTypeFormInvalid(this.typeForm) === true) return;
    const value = buildTypePayload(this.typeForm.value);
    this.evaluationService.upsertQuestionType(value).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.onTypeSaved(),
      error: (err: any) => this.onTypeSaveError(err),
    });
  }

  /** Cierra modal y recarga tipos. */
  private onTypeSaved() {
    this.modalService.dismissAll();
    this.loadTypes();
  }

  /** Muestra error de guardado. */
  private onTypeSaveError(err: any) {
    showTypeError(err, 'No se pudo guardar');
  }

  toggle(type: any) {
    const enabled = !type.enabled;
    this.evaluationService.toggleQuestionType(type.id, enabled).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.loadTypes(),
      error: (err: any) => this.onToggleError(err),
    });
  }

  /** Muestra error de cambio de estado. */
  private onToggleError(err: any) {
    showTypeError(err, 'Error al cambiar estado');
  }

  /** Muestra error de borrado. */
  private onRemoveError(err: any) {
    showTypeError(err, 'Error al eliminar');
  }

  confirmRemove(type: any) {
    confirmRemoveType((ok) => this.onRemoveConfirmed(ok, type));
  }

  /** Borra tipo si fue confirmado. */
  private onRemoveConfirmed(ok: boolean, type: any) {
    if (ok === true) this.remove(type.id);
  }

  remove(id: string) {
    this.evaluationService.deleteQuestionType(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.loadTypes(),
      error: (err: any) => this.onRemoveError(err),
    });
  }
}
